import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertTriangle, IconSearch, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreatePracticalInformationExport } from 'src/Hooks/Mutations/useCreatePracticalInformationExport';
import { useDeletePracticalInformationExport } from 'src/Hooks/Mutations/useDeletePracticalInformationExport';
import useFetchAllPracticalInformation from 'src/Hooks/Queries/useFetchAllPracticalInformation';
import useFetchPracticalInformationExports from 'src/Hooks/Queries/useFetchPracticalInformationExports';
import { PracticalInformationPreviewDto } from 'src/Services/API/generated';

import styles from './CreateBookletModal.module.scss';
import { Props } from './CreateBookletModal.types';

const EMERGENCY_SUBTYPES = [
  'EMERGENCY_CONTACTS',
  'UTILITY_METERS_AND_VALVES_LOCATION',
  'ALARM_AND_SURVEILLANCE_SYSTEM',
  'FIRE_EXTINGUISHERS_AND_DETECTORS',
  'FIRST_AID_KIT_LOCATION',
  'EMERGENCY_EXITS_AND_EVACUATION_PLAN',
  'EMERGENCY_INSTRUCTIONS',
  'TROUBLESHOOTING_INSTRUCTIONS',
  'WATER_HEATER_BOILER_OPERATION',
];

const CreateBookletModal = ({ opened, onClose, onShowQrCode }: Props) => {
  const { t } = useTranslation();
  const { data: allInformation } = useFetchAllPracticalInformation();
  const { data: existingExports } = useFetchPracticalInformationExports();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const { mutate: createExport, isLoading: isCreating } = useCreatePracticalInformationExport({
    onSuccess: data => {
      setSelectedIds([]);
      setSearch('');
      setExpiresAt(null);
      onShowQrCode(data.publicUrl);
    },
  });
  const { mutate: deleteExport } = useDeletePracticalInformationExport();

  const getInfoLabel = useCallback(
    (info: PracticalInformationPreviewDto) =>
      info.subtype ? t(`practicalInformation.subtypes.${info.subtype}`) : (info.title ?? ''),
    [t]
  );

  const filteredInformation = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allInformation;
    return allInformation.filter(info => getInfoLabel(info).toLowerCase().includes(term));
  }, [allInformation, search, getInfoLabel]);

  const handleEmergencyPreselect = () => {
    const ids = allInformation
      .filter(info => info.subtype && EMERGENCY_SUBTYPES.includes(info.subtype))
      .map(info => info.id);
    setSelectedIds(ids);
  };

  const handleCreate = () => {
    if (selectedIds.length === 0) return;
    createExport({
      entityIds: selectedIds,
      expiresAt: expiresAt ? dayjs(expiresAt).toISOString() : undefined,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('booklet.create.title', 'Créer un livret')}
      size="lg"
    >
      <div className={styles.container}>
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder={t('booklet.create.searchPlaceholder', 'Rechercher une info pratique')}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
        />

        {allInformation.length === 0 ? (
          <Text className={styles.emptyState} c="dimmed">
            {t('booklet.create.noInformation', 'Aucune info pratique à mettre dans un livret.')}
          </Text>
        ) : (
          <>
            <div className={styles.quickSelectRow}>
              <Text size="sm" c="dimmed">
                {t('booklet.create.quickSelectLabel', 'Sélection rapide :')}
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconAlertTriangle size={14} />}
                onClick={handleEmergencyPreselect}
              >
                {t('booklet.create.emergencyPreselect', 'Infos essentielles en cas d’urgence')}
              </Button>
            </div>
            <ScrollArea.Autosize mah={280}>
              <Checkbox.Group value={selectedIds} onChange={setSelectedIds}>
                <Stack gap="xs">
                  {filteredInformation.map(info => (
                    <Checkbox key={info.id} value={info.id} label={getInfoLabel(info)} />
                  ))}
                </Stack>
              </Checkbox.Group>
            </ScrollArea.Autosize>
          </>
        )}

        <DateInput
          label={t('booklet.create.expiresAt', "Date d'expiration (optionnel)")}
          placeholder={t('booklet.create.noExpiry', "Pas d'expiration")}
          value={expiresAt}
          onChange={setExpiresAt}
          valueFormat="DD/MM/YYYY"
          minDate={new Date()}
          clearable
        />

        <Button onClick={handleCreate} loading={isCreating} disabled={selectedIds.length === 0}>
          {t('booklet.create.submit', 'Créer le livret')} ({selectedIds.length})
        </Button>

        {existingExports.length > 0 && (
          <div className={styles.existingSection}>
            <Text fw={600}>{t('booklet.create.existingTitle', 'Livrets existants')}</Text>
            <Stack gap="xs">
              {existingExports.map(bookletExport => (
                <Group key={bookletExport.id} justify="space-between" wrap="nowrap">
                  <Text size="sm">
                    {dayjs(bookletExport.createdAt).format('LL')} —{' '}
                    {t('booklet.create.infoCount', '{{count}} info(s)', {
                      count: bookletExport.entityIds?.length ?? 0,
                    })}
                  </Text>
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => onShowQrCode(bookletExport.publicUrl)}
                    >
                      {t('booklet.create.showQr', 'Voir le QR')}
                    </Button>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label={t('booklet.create.delete', 'Supprimer le livret')}
                      onClick={() => deleteExport(bookletExport.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
            </Stack>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateBookletModal;
